// React and hooks
import React, { useEffect, useState, useRef } from 'react';
// Redux
import { useSelector, useDispatch } from 'react-redux';

// Routing
import { NavLink } from 'react-router-dom';

// UI Components (from React Bootstrap)
import { Row, Col, Button, Dropdown, Form } from 'react-bootstrap';

// Utils and error handlers
import { handlerErrors } from 'utils/handleErrors';
import { cleanShippingId } from 'utils/textFormat';
import moment from 'moment';

// Custom Components
import HtmlHead from 'components/html-head/HtmlHead';
import RainCalendar from 'components/calendars/RainCalendar';
import CsLineIcons from 'cs-line-icons/CsLineIcons';
import AdminLoader from 'components/loading/AdminLoader';
import LoadingPulse from 'components/loading/LoadingPulse';
import Paginate from '../../components/Paginate';
import ChargesRow from './components/ChargesRow';
import ChargesCard from './components/ChargesCard';

// Redux Actions
import { setPage, setItemsPerPage } from '../../../slices/uiStates/additionalChargesSlice';
// API calls
import { getPendingAdditionalCharges, getFilteredPendingAdditionalCharges } from '../../api/Api-admin';
// import { ToastSuccess, ToastErrorApi, ToastErrorCath } from '../../../components/alerts/toast';
const { FormatMoney } = require('format-money-js');

const AdditionalChargesPending = () => {
  const title = 'Cargos Pendientes';
  const description = 'Cargos Adicionales Plataforma Autopaquete';
  const dispatch = useDispatch();
  // - Global State from Store.js
  const { page, itemsPerPage } = useSelector((state) => state.all_charges_pending);
  // - Local States
  const [additionalCharges, setAdditionalCharges] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  // Start Pagination States
  const [totalPages, setTotalPages] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const [pageView, setPageView] = useState(page); // solo para la vista
  const [itemsPerPageView, setItemsPerPageView] = useState(itemsPerPage); // solo para la vista
  // Start Filter States
  const [crmId, setCrmId] = useState('');
  const [shippingId, setShippingId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filteredMode, setFilteredMode] = useState(false);
  // End Filter States
  // Start States for Components for Filtering
  const [filteredSuccess, setFilteredSuccess] = useState('');
  const [selectStartDate, setSelectStartDate] = useState({ date: undefined });
  const [selectEndDate, setSelectEndDate] = useState({ date: undefined });
  // End States for Components for Filtering
  // - Use references to do scroll Up
  const startRef = useRef(null);
  const fm = new FormatMoney({
    decimals: 0, // No decimales
  });
  // Reset Values
  const resetInitialValues = () => {
    setSelectStartDate({ date: undefined });
    setSelectEndDate({ date: undefined });
    setStartDate('');
    setEndDate('');
    setShippingId('');
    setCrmId('');
    setFilteredSuccess(''); // para eliminar el mensaje "Filtro por nombre..."
  };
  const filterByShippingId = () => {
    if (startDate) {
      setSelectEndDate({ date: undefined });
      setSelectStartDate({ date: undefined });
      setStartDate('');
      setEndDate('');
    }
  };
  const handlerGetAdditionalCharges = async () => {
    try {
      setIsLoading(true);
      const response = await getPendingAdditionalCharges(page, itemsPerPage);
      // console.log('Get additionalCharges pendientes response', response);
      if (response?.data.additional_charges !== undefined) {
        setAdditionalCharges(response?.data.additional_charges);
        setTotalPages(response?.data.total_pages);
        setPageView(response?.data.current_page); // para actualizar la vista
        setItemsPerPageView(response?.data.page_size); // para actualizar la vista
        setTotalItems(response?.data.total_items);
        if (startRef.current) {
          startRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } catch (error) {
      handlerErrors(error);
      setAdditionalCharges(additionalCharges);
    } finally {
      setIsLoading(false);
    }
  };
  const handlerGetFilteredAdditionalCharges = async () => {
    try {
      setIsLoading(true);
      // console.log('Get additionalCharges filtred request:', shippingId, crmId, startDate, endDate);
      const response = await getFilteredPendingAdditionalCharges(page, itemsPerPage, crmId, shippingId, startDate, endDate);
      // console.log('Get additionalCharges filtred response', response);
      if (response?.data.additional_charges !== undefined) {
        setAdditionalCharges(response?.data.additional_charges);
        setTotalPages(response?.data.total_pages);
        setPageView(response?.data.current_page); // para actualizar la vista
        setItemsPerPageView(response?.data.page_size); // para actualizar la vista
        setTotalItems(response?.data.total_items);
        setFilteredSuccess(
          <>
            Filtro
            {shippingId && (
              <>
                {' '}
                Id de envío ▸ <strong>{shippingId}</strong>
              </>
            )}
            {crmId && (
              <>
                {' '}
                Id Usuario ▸ <strong>{crmId}</strong>
              </>
            )}
            {startDate && (
              <>
                {' '}
                del <strong>{moment(startDate).format('DD-MM-YY')}</strong>
              </>
            )}
            {endDate && (
              <>
                {' '}
                al <strong>{moment(endDate).format('DD-MM-YY')}</strong>
              </>
            )}
          </>
        );
        if (startRef.current) {
          startRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } catch (error) {
      handlerErrors(error);
      setAdditionalCharges(additionalCharges);
    } finally {
      setFilteredMode(true); // respuesta exitosa o no es necesario para mantener el modo filtrando y que aparezca el btn regresar
      setIsLoading(false);
    }
  };
  const settingPage = (limit) => {
    setIsLoading(true);
    dispatch(setItemsPerPage(limit)); // ejecuta el useEffect que decide que funcion ejecutar
    dispatch(setPage(1)); // ejecuta el useEffect que decide que funcion ejecutar
  };
  const resetInitialMode = () => {
    setFilteredMode(false);
    resetInitialValues();
    if (page > 1) {
      dispatch(setPage(1)); // ejecutara el useEffect que decide que fn ejecutar pero con la pag 1
    } else {
      // si page es 1 no ejecuta el cambio de estado ni el useEffect es necesario llamar a la funcion
      handlerGetAdditionalCharges();
    }
  };
  // - Whenever the pagination status change
  useEffect(() => {
    if (filteredMode) {
      handlerGetFilteredAdditionalCharges();
    } else {
      handlerGetAdditionalCharges();
    }
  }, [page, itemsPerPage]);

  return (
    <>
      {isLoading && <LoadingPulse />}
      {additionalCharges === null ? (
        <AdminLoader />
      ) : (
        <>
          <HtmlHead title={title} description={description} />
          <div className="page-title-container" ref={startRef}>
            <Row className="g-0">
              {/* Title Start */}
              <Col className="col-auto mb-3 mb-sm-0 me-auto">
                <NavLink className="muted-link pb-1 d-inline-block hidden breadcrumb-back invisible" to="/">
                  <CsLineIcons icon="chevron-left" size="13" />
                  <span className="align-middle text-small ms-1">Inicio</span>
                </NavLink>
                <h1 className="mb-0 pb-0 display-4" id="title">
                  {title}
                </h1>
              </Col>
              {/* Title End */}
            </Row>
          </div>
          <div style={{ minHeight: '350px' }} id="cardContainer">
            {/* *** Dropdown and Search Buttons Start *** */}
            <Row className="g-0 mb-2 mt-sm-5">
              {/* Filters Start */}
              <Col xs="auto" xl="9">
                {/* Shipping id, crm id and dates Start */}
                <Row className="g-0 bg-successx">
                  <Col xs="12" sm="auto" className="pe-2">
                    <Row className="g-0">
                      <Col xs="12" sm="6" className="d-inline-block pe-2">
                        <Form.Control
                          type="text"
                          className="mb-2"
                          // style={{ fontFamily: 'Montserrat', width: '275px' }}
                          placeholder="ID Usuario"
                          // readOnly={disabledSearchInput}
                          value={crmId}
                          onChange={(e) => setCrmId(e.target.value)}
                        />
                      </Col>
                      <Col xs="12" sm="6" className="pe-2 mb-2">
                        <Form.Control
                          type="text"
                          // style={{ fontFamily: 'Montserrat', width: '275px' }}
                          placeholder="ID Envío "
                          // readOnly={disabledSearchInput}
                          value={shippingId}
                          onChange={(e) => {
                            setShippingId(cleanShippingId(e.target.value));
                            filterByShippingId();
                          }}
                        />
                      </Col>
                    </Row>
                    <Row className="g-0">
                      <Col xs="12" sm="6" lg="auto" className="pt-0 mb-2 mb-xl-0 pe-2">
                        <RainCalendar
                          selectDate={selectStartDate}
                          setSelectDate={setSelectStartDate}
                          setState={setStartDate}
                          state={startDate}
                          holder="Fecha Inicial"
                          // label="Fecha Inicial"
                          // isDisabled={disabledCalendar}
                        />
                      </Col>
                      <Col xs="12" sm="6" lg="auto" className="mb-2 mb-xl-0 pe-2">
                        <RainCalendar
                          selectDate={selectEndDate}
                          setSelectDate={setSelectEndDate}
                          setState={setEndDate}
                          holder="Fecha Final"
                          // label="Fecha Final"
                          // isDisabled={disabledCalendar}
                        />
                      </Col>
                    </Row>
                  </Col>
                  {/* Filter buttons Start */}
                  <Col xs="12" sm="auto" className="pb-2 pb-xl-0 d-flex align-items-end">
                    <Row className="g-0">
                      <Col xs="auto" className="pe-2">
                        <Button
                          variant="primary"
                          style={{ height: '36px' }}
                          className="w-100 mb-0 mt-2"
                          onClick={() => {
                            // handlerGetFilteredShippings();
                            if (page > 1) {
                              // la primera vez el estado filteredMode es falso
                              setFilteredMode(true); // para que el useEffect funcione bien
                              dispatch(setPage(1)); // ejecutara el useEffect que decide que fn ejecutar pero con la pag 1
                            } else {
                              handlerGetFilteredAdditionalCharges();
                            }
                          }}
                        >
                          <span>Buscar</span>
                        </Button>
                      </Col>
                      {filteredMode && (
                        <Col xs="auto" className="p-0">
                          <Button variant="outline-primary" style={{ height: '36px' }} className="w-100 mb-0 mt-2" onClick={resetInitialMode}>
                            <span>Regresar</span>
                          </Button>
                        </Col>
                      )}
                    </Row>
                  </Col>
                  {/* Name to, crm id and dates Start */}
                </Row>
                {/* Filter buttons End */}
              </Col>
              {/* Filters End */}
              {/* Items selector Start */}
              <Col xs="12" xl="3" className="pb-2 pt-sm-5 pt-xl-0 pb-xl-0 d-flex justify-content-end align-items-end bg-infow">
                <Row className="g-0">
                  <Col xs="auto" className="p-0 ms-2">
                    <Dropdown align={{ xs: 'end' }} className="d-inline-block ms-1">
                      <Dropdown.Toggle variant="foreground-alternate" className={`shadow sw-20 ${additionalCharges?.length < 2 && 'invisible'}`}>
                        {`${itemsPerPageView} Cargos`}
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="shadow dropdown-menu-end">
                        <Dropdown.Item className={`${itemsPerPageView === 10 && 'd-none'}`} onClick={() => settingPage(10)}>
                          10 Cargos
                        </Dropdown.Item>
                        <Dropdown.Item className={`${itemsPerPageView === 20 && 'd-none'}`} onClick={() => settingPage(20)}>
                          20 Cargos
                        </Dropdown.Item>
                        <Dropdown.Item className={`${itemsPerPageView === 50 && 'd-none'}`} onClick={() => settingPage(50)}>
                          50 Cargos
                        </Dropdown.Item>
                        <Dropdown.Item className={`${itemsPerPageView === 100 && 'd-none'}`} onClick={() => settingPage(100)}>
                          100 Cargos
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                    {/* Length End */}
                  </Col>
                  {/* <div className={`text-end text-muted d-lg-none mt-3 mb-2 ${shippings?.length < 2 && 'invisible'}`}>{`Página ${currentPage}`}</div> */}
                </Row>
              </Col>
              {/* Items selector End */}
            </Row>
            <hr className="mt-3 mb-4 mt-xl-5 bg-muted" />
            {/* *** Dropdown and Search Buttons End *** */}
            {/* *** Main Page Start *** */}
            {filteredSuccess && <div className="text-muted mb-3">{filteredSuccess}</div>}
            {/* <div className={`d-flex flex-column justify-content-between mb-3 text-muted  ${additionalCharges?.length < 2 && 'd-none'}`}>
              <span>{`Página ${pageView} `}</span>
              <span>{`Total: ${fm.from(totalItems)}`}</span>
            </div> */}
            <div className={`d-flex flex-column justify-content-between mb-3 text-muted `}>
              <span>
                Página <strong>{pageView}</strong>
              </span>
              <span>
                Total: <strong>{fm.from(totalItems)}</strong>
              </span>
            </div>
            {/* List Header Start */}
            <Row className="g-0 mb-2 d-none d-lg-flex">
              <ChargesRow />
              {/* </Col> */}
            </Row>
            {/* List Header End */}
            {additionalCharges?.length === 0 ? (
              <div className="py-5 mt-5 d-flex flex-column justify-content-center align-items-center w-50 m-auto">
                <h3 className="text-center mt-5">La lista de cargos adicionales está vacía</h3>
              </div>
            ) : (
              <>
                {/* List Items Start  */}
                {additionalCharges?.map((item, index) => (
                  <ChargesCard key={index} data={item} />
                ))}
                {/* List Items End */}
                {/* Pagination Start */}
                <div className={`d-flex flex-column justify-content-center align-items-center mt-5 mx-auto ${totalPages < 2 && 'd-none'}`}>
                  <Paginate currentPage={pageView} totalPages={totalPages} setPageReducer={setPage} />
                </div>
                {/* Pagination End */}
              </>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default AdditionalChargesPending;
